import { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { ProjectReader } from '../lib/types';
import type { Reading } from '../services/readings';
import { deleteReading } from '../services/readings';
import { Badge, Button, Modal } from './ui';

/**
 * What a reader-driven project has recorded, one section per reader.
 *
 * Each section has that reader's own columns, because two readers rarely
 * measure the same things. A reader removed from the project still shows here
 * if it has readings: removing it stops new captures, not the record.
 */
export default function ReadingsView({
  projectId,
  readers,
  readings,
  canDelete,
}: {
  projectId: string;
  readers: ProjectReader[];
  readings: Reading[];
  /** Only the owner may remove a reading, by the project rules. */
  canDelete: boolean;
}) {
  const [removing, setRemoving] = useState<Reading | null>(null);

  const sections = useMemo(() => {
    const byReader = new Map<string, Reading[]>();
    for (const r of readings) {
      const list = byReader.get(r.readerId) ?? [];
      list.push(r);
      byReader.set(r.readerId, list);
    }
    const known = new Map(readers.map((r) => [r.id, r]));
    const order = [
      ...readers.map((r) => r.id),
      ...[...byReader.keys()].filter((id) => !known.has(id)),
    ];
    return order.map((id) => {
      const rows = byReader.get(id) ?? [];
      const reader = known.get(id);
      // A reader no longer on the project still labels its columns from the
      // keys its readings carry.
      const fields =
        reader?.fields.map((f) => ({ key: f.key, label: f.label, unit: f.unit, decimals: f.decimals })) ??
        [...new Set(rows.flatMap((r) => Object.keys(r.values)))].map((key) => ({
          key,
          label: key,
          unit: '',
          decimals: 3,
        }));
      // Samples in the order they were first recorded, replicates ascending
      // within each. Readings saved together share a timestamp, so time alone
      // cannot order them.
      const firstSeen = new Map<string, number>();
      [...rows].reverse().forEach((r, i) => {
        if (!firstSeen.has(r.sampleLabel)) firstSeen.set(r.sampleLabel, i);
      });
      const sorted = [...rows].sort(
        (a, b) =>
          (firstSeen.get(a.sampleLabel) ?? 0) - (firstSeen.get(b.sampleLabel) ?? 0) ||
          a.sampleLabel.localeCompare(b.sampleLabel, undefined, { numeric: true }) ||
          a.replicate - b.replicate,
      );
      const hasSummary = rows.some((r) => r.summary);
      return {
        id,
        name: reader?.name ?? rows[0]?.readerName ?? 'Readings',
        fields,
        rows: sorted,
        hasSummary,
      };
    });
  }, [readers, readings]);

  return (
    <div className="space-y-5">
      {sections.map((s) => (
        <section key={s.id} className="rounded-xl border border-ink-700 bg-ink-900/40">
          <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-2.5">
            <h2 className="text-sm font-semibold">{s.name}</h2>
            <Badge tone="neutral">
              {s.rows.length} reading{s.rows.length === 1 ? '' : 's'}
            </Badge>
          </div>
          {s.rows.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-ink-400">
              Nothing recorded yet. Use the bar below to add the first reading.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-400">
                    <th className="px-4 py-2 font-medium">Sample</th>
                    <th className="px-2 py-2 font-medium">{s.hasSummary ? 'N' : '#'}</th>
                    {s.fields.map((f) => (
                      <th key={f.key} className="whitespace-nowrap px-2 py-2 text-right font-medium">
                        {f.label}
                        {f.unit ? ` (${f.unit})` : ''}
                      </th>
                    ))}
                    {canDelete && <th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((r) => (
                    <tr key={r.id} className="border-t border-ink-800">
                      <td className="max-w-48 truncate px-4 py-1.5 font-mono text-xs">{r.sampleLabel}</td>
                      <td className="px-2 py-1.5 tabular-nums text-xs text-ink-500">
                        {r.summary ? (r.n ?? '—') : r.replicate}
                      </td>
                      {s.fields.map((f) => (
                        <td key={f.key} className="px-2 py-1.5 text-right font-mono text-xs tabular-nums">
                          {typeof r.values[f.key] === 'number' ? (
                            <>
                              {r.values[f.key].toFixed(Math.min(6, Math.max(0, f.decimals)))}
                              {r.summary && typeof r.sd?.[f.key] === 'number' && (
                                <span className="text-ink-500">
                                  {' ± '}
                                  {r.sd[f.key].toFixed(Math.min(6, Math.max(0, f.decimals)))}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-ink-600">—</span>
                          )}
                        </td>
                      ))}
                      {canDelete && (
                        <td className="px-1 py-1">
                          <button
                            onClick={() => setRemoving(r)}
                            className="icon-btn text-ink-500 hover:bg-red-950 hover:text-red-300"
                            aria-label={`Delete reading ${r.replicate} of ${r.sampleLabel}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      <Modal open={!!removing} onClose={() => setRemoving(null)} title="Delete reading">
        <p className="text-sm text-ink-300">
          Delete reading {removing?.replicate} of{' '}
          <strong className="text-ink-100">{removing?.sampleLabel}</strong>? It cannot be undone.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (removing) await deleteReading(projectId, removing.id);
              setRemoving(null);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
